import { ActivityParentEntityId, NoteEntityId, NoteId } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ActivityEntityType } from "components/entity-activity/types";
import { Header } from "components/header";
import { NotesInput } from "components/notes/notes-input";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    GetNotesForEntityQuery,
    UpdateNoteMutation,
    useGetActivityFeedQuery,
    useGetNotesForEntityQuery,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { LogActivityForm } from "./log-activity-form";
import { UnifiedTimelineFeed } from "./unified-timeline-feed";

interface UnifiedTimelineProps {
    entityId: ActivityParentEntityId;
    entityType: ActivityEntityType;
}

export const UnifiedTimeline: React.FC<UnifiedTimelineProps> = props => {
    const { entityId, entityType } = props;
    const noteEntityId = entityId as NoteEntityId;
    const notesQuery = useGetNotesForEntityQuery({ entityId: noteEntityId });
    const activityQuery = useGetActivityFeedQuery({ entityId });
    const queryClient = useQueryClient();
    const [localDeletes, setLocalDeletes] = React.useState<NoteId[]>([]);
    const [showLogActivity, setShowLogActivity] = React.useState(false);

    const refetch = React.useCallback(() => {
        notesQuery.refetch();
        activityQuery.refetch();
    }, [notesQuery, activityQuery]);

    const handleNoteChanged = React.useCallback(
        (note: GetNotesForEntityQuery["getNotesForEntity"][0]) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId: noteEntityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        let replaced = false;
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                replaced = true;
                                return note;
                            }
                            return n;
                        });
                        if (!replaced) {
                            draft.getNotesForEntity.unshift(note);
                        }
                    });
                },
            );
        },
        [queryClient, noteEntityId],
    );

    const handleNoteDeleted = React.useCallback((noteId: NoteId) => {
        setLocalDeletes(d => [...d, noteId]);
    }, []);

    const handleNoteUpdated = React.useCallback(
        (note: UpdateNoteMutation["updateNote"]) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId: noteEntityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                return note;
                            }
                            return n;
                        });
                    });
                },
            );
        },
        [queryClient, noteEntityId],
    );

    return (
        <QueryRenderer name="UnifiedTimeline" query={notesQuery}>
            {notesData => (
                <QueryRenderer name="UnifiedTimelineActivity" query={activityQuery}>
                    {activityData => {
                        const allNotes = notesData.getNotesForEntity
                            .filter(n => !localDeletes.includes(n._id))
                            .sort(
                                (a, b) =>
                                    new Date(b.dateAdded).getTime() -
                                    new Date(a.dateAdded).getTime(),
                            );

                        return (
                            <InputFormBlock additionalClassName="overflow-y-scroll max-h-140">
                                <Header
                                    iconName="09_Notes"
                                    action={
                                        <SecondaryButton
                                            className="text-sm !py-1 !px-3"
                                            onClick={() => setShowLogActivity(s => !s)}>
                                            Log Activity
                                        </SecondaryButton>
                                    }>
                                    Activity
                                </Header>
                                {showLogActivity ? (
                                    <LogActivityForm
                                        entityId={entityId}
                                        onComplete={() => {
                                            setShowLogActivity(false);
                                            refetch();
                                        }}
                                        onCancel={() => setShowLogActivity(false)}
                                    />
                                ) : null}
                                <div data-testid="unified-timeline">
                                    <NotesInput
                                        refetch={refetch}
                                        entityId={noteEntityId}
                                        initial={allNotes}
                                        onNoteChanged={handleNoteChanged}
                                    />
                                    <UnifiedTimelineFeed
                                        entityId={entityId}
                                        entityType={entityType}
                                        activities={
                                            activityData.getActivityFeed.activities
                                        }
                                        notes={allNotes}
                                        refetch={refetch}
                                        onNoteDeleted={handleNoteDeleted}
                                        onNoteUpdated={handleNoteUpdated}
                                        localDeletes={localDeletes}
                                    />
                                </div>
                            </InputFormBlock>
                        );
                    }}
                </QueryRenderer>
            )}
        </QueryRenderer>
    );
};
